import _ from 'lodash';
import moment from 'moment';
import globalStore from '$$globalStore';
import constants from '$$helpers/constants';
import tasksHelpers, { REQUIRED_TASK_FIELDS } from '$$store/tasks/helpers';
import GanttWorker from '../workers/GanttWorker';
import ProgressWorker from '../workers/ProgressWorker';
import prepareTaskResources from './prepareTaskResources';

const prepareNewTaskData = (task, ganttId, ownerId) => {
  const taskData = {
    id: task.id || tasksHelpers.uid(),
    parent: task.parent,
    gantt_id: +ganttId,
    type: task.type || constants.TASK_TYPES.task,
    open: 1,
    text: task.text?.trim() || tasksHelpers.getDefaultNewTaskTextByType(task.type || constants.TASK_TYPES.task),
    color: task.color || tasksHelpers.getDefaultNewTaskColorByType(task.type || constants.TASK_TYPES.task),
    start_date: task.start_date || moment().milliseconds(0).toDate(),
    end_date: task.end_date,
    progress: task.progress || 0,
    estimation: task.estimation || 0,
    duration: task.duration,
    sortorder: task.sortorder,
    owner_id: ownerId,
    note: task.note || '',
    deadline: task.deadline || null,
    hasAttachments: task.hasAttachments || 0,
    hasComments: task.hasComments || 0,
    newComments: task.newComments || null,
    created_at: task.created_at || moment().milliseconds(0).toDate(),
  };

  if (taskData.type === constants.TASK_TYPES.task || taskData.type === constants.TASK_TYPES.milestone) {
    taskData.total_price = 0;
    taskData.actual_cost = 0;
    taskData.jira_key = '';
  }

  if (task.unit) {
    taskData.unit = task.unit;
  }

  return taskData;
};

export default async function (tasks, fromGantt) {
  const tasksMap = globalStore.getters['tasksModel/tasksByIds'];
  const ownerId = globalStore.getters['resourcesModel/getResourceIdByUserId'](user.id);

  const createdMap = {};
  const updatedMap = {};
  const createdTasksForCommit = [];
  const updatedTasksForCommit = [];
  const resourcesToTasksForCommit = [];
  const tasksCustomValuesForCommit = [];
  const userCustomValuesForCommit = [];
  let refreshOverload = false;

  // Tasks creation
  tasks.forEach(task => {
    const ganttId = +task.gantt_id || +tasksMap[task.parent]?.gantt_id;
    const { status: defaultStatus, priority: defaultPriority } = globalStore.getters['tasksModel/getDefaultCustomValues'](ganttId);

    if (!task.parent) {
      throw 'Task parent (parent) is not provided for creation';
    }

    if (!ganttId) {
      throw 'Project id (ganttId) is not provided for task creation';
    }

    const taskData = prepareNewTaskData(task, ganttId, ownerId);
    const diff = _.pick(taskData, REQUIRED_TASK_FIELDS);

    if (taskData.type === constants.TASK_TYPES.task || taskData.type === constants.TASK_TYPES.milestone) {
      taskData.status = task.status || defaultStatus;
      taskData.priority = task.priority || defaultPriority;

      const tasksCustomValues = {};

      if (task.status !== defaultStatus) {
        diff.status = task.status;
        tasksCustomValues.status = task.status;
      }

      if (task.priority !== defaultPriority) {
        diff.priority = task.priority;
        tasksCustomValues.priority = task.priority;
      }

      if (!_.isEmpty(tasksCustomValues)) {
        tasksCustomValuesForCommit.push({ taskId: taskData.id, ganttId: taskData.gantt_id, tasksCustomValues });
      }
    }

    if (taskData.type === constants.TASK_TYPES.task || taskData.type === constants.TASK_TYPES.milestone) {
      taskData.resources = [];

      if (!_.isEmpty(task.changedResources)) {
        diff.changedResources = task.changedResources;
        taskData.resources = prepareTaskResources(taskData.id, taskData.gantt_id, task.changedResources);

        resourcesToTasksForCommit.push({ taskId: taskData.id, ganttId: taskData.gantt_id, resourcesToTask: taskData.resources });

        refreshOverload = true;
      }
    }

    if (!_.isEmpty(task.customFields)) {
      diff.customFields = task.customFields;

      userCustomValuesForCommit.push({ taskId: taskData.id, ganttId: taskData.gantt_id, customFields: task.customFields });
    }

    createdMap[taskData.id] = diff;
    createdTasksForCommit.push(taskData);
  });

  // Run workers (if needed)
  const needGanttWorker = !fromGantt && createdTasksForCommit.length;
  const needProgressWorker = createdTasksForCommit.length;
  let ganttWorkerData = null;
  let progressWorkerData = null;

  if (needGanttWorker) {
    ganttWorkerData = await GanttWorker.calculate(createdTasksForCommit, 'changeTasks', 'addTasks');
    fromGantt = false;
  }

  createdTasksForCommit.forEach(task => {
    if (ganttWorkerData && ganttWorkerData.diff.tasksToAdd[task.id]) {
      _.merge(task, ganttWorkerData.diff.tasksToAdd[task.id]);
      _.merge(createdMap[task.id], ganttWorkerData.diff.tasksToAdd[task.id]);
    }
  });

  if (needProgressWorker) {
    progressWorkerData = await ProgressWorker.calculate(createdTasksForCommit, 'changeTasks', 'addTasks');
    fromGantt = false;
  }

  createdTasksForCommit.forEach(task => {
    if (progressWorkerData && progressWorkerData.diff.tasksToAdd[task.id]) {
      _.merge(task, progressWorkerData.diff.tasksToAdd[task.id]);
      _.merge(createdMap[task.id], progressWorkerData.diff.tasksToAdd[task.id]);
    }
  });

  if (ganttWorkerData || progressWorkerData) {
    const combinedData = _.merge(ganttWorkerData?.diff?.tasks, progressWorkerData?.diff?.tasks);

    _.forOwn(combinedData, (diff, taskId) => {
      if (!updatedMap[taskId]) {
        updatedMap[taskId] = diff;
        updatedTasksForCommit.push({ id: +taskId, ...tasksMap[taskId], ...diff });
      }

      if (tasksMap[taskId] && tasksMap[taskId].resources?.length && !refreshOverload) {
        refreshOverload = _.has(diff, 'start_date') || _.has(diff, 'duration');
      }
    });
  }

  return {
    createdMap, updatedMap, createdTasksForCommit, updatedTasksForCommit, resourcesToTasksForCommit, tasksCustomValuesForCommit, userCustomValuesForCommit, refreshOverload, fromGantt,
  };
}
